import { useMemo } from 'react';
import { startOfDay, endOfDay } from 'date-fns';
import { CalendarClock, ListTodo, Users } from 'lucide-react';
import { useAuthStore } from '@/app/store/authStore';
import { TaskStatus } from '@/app/types/task';
import TaskPanel from './TaskPanel';

/**
 * The four actionable task panels shown on both the standard and client-manager
 * dashboards:
 *   - My Tasks Due Today
 *   - My Team's Tasks Due Today
 *   - My Tasks (open)
 *   - My Team's Tasks (unclaimed team pool)
 */
export default function DashboardTaskPanels() {
  const user = useAuthStore((state) => state.user);
  const myId = user?.id ? parseInt(user.id, 10) : undefined;

  const { dueFrom, dueTo } = useMemo(() => {
    const now = new Date();
    return {
      dueFrom: startOfDay(now).toISOString(),
      dueTo: endOfDay(now).toISOString(),
    };
  }, []);

  if (!myId) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <TaskPanel
        title="My Tasks Due Today"
        icon={CalendarClock}
        queryKey={['tasks', 'panel', 'my-due-today', myId]}
        fetchParams={{
          assigned_to_user_id: myId,
          status: TaskStatus.OPEN,
          due_date_from: dueFrom,
          due_date_to: dueTo,
        }}
        viewMoreHref={`/tasks?assigned_to_user_id=${myId}&due=today`}
        emptyText="Nothing due today — nice work"
      />
      <TaskPanel
        title="My Team's Tasks Due Today"
        icon={CalendarClock}
        queryKey={['tasks', 'panel', 'team-due-today', myId]}
        fetchParams={{
          unassigned_in_my_teams: true,
          status: TaskStatus.OPEN,
          due_date_from: dueFrom,
          due_date_to: dueTo,
        }}
        viewMoreHref="/tasks?team_pool=1&due=today"
        emptyText="No team tasks due today"
      />
      <TaskPanel
        title="My Tasks"
        icon={ListTodo}
        queryKey={['tasks', 'panel', 'my-tasks', myId]}
        fetchParams={{
          assigned_to_user_id: myId,
          status: TaskStatus.OPEN,
          sort_by: 'due_date',
          sort_dir: 'asc',
        }}
        viewMoreHref={`/tasks?assigned_to_user_id=${myId}`}
        emptyText="No open tasks assigned to you"
      />
      <TaskPanel
        title="My Team's Tasks"
        icon={Users}
        queryKey={['tasks', 'panel', 'team-pool', myId]}
        fetchParams={{
          unassigned_in_my_teams: true,
          status: TaskStatus.OPEN,
          sort_by: 'due_date',
          sort_dir: 'asc',
        }}
        viewMoreHref="/tasks?team_pool=1"
        emptyText="No unclaimed tasks in your teams"
      />
    </div>
  );
}
